import {
    Box,
    Button,
    Dialog, DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Paper,
    TextField,
    Typography
} from "@mui/material";
import {Close, Fingerprint, Lock} from "@mui/icons-material";
import React, {useEffect, useState} from "react";
import {WebcamCapture} from "../management/people/components/WebcamCapture";
import {useForm} from "react-hook-form";
import {useCommonItems} from "../../../contexts/CommonItensProvider";
import {atualizaUsuario, getPessoa} from "../management/people/configs/functions";

export function FirstAccess({
                                open,
                                setOpen,
                                onClose,
                                reloadPessoa,
                                selectedPessoa,
                                justBioface
                            }) {
    const { exibirAlerta, exibirDialog, handleCloseDialog } = useCommonItems();

    const [isAllowedCapture, setIsAllowedCapture] = useState(false);
    const [allowInteraction, setAllowInteraction] = useState(true);

    const [biometriaSrc, setBiometriaSrc] = useState(null);
    const [senha, setSenha] = useState("");
    const [confirmarSenha, setConfirmarSenha] = useState("");

    const [erroBioface, setErroBioface] = useState("");
    const [erroSenha, setErroSenha] = useState("");

    const isAdmin = selectedPessoa?.tipoUsuario === 'administrador'
        || selectedPessoa?.tipo_usuario === 'administrador';

    const minSenha = isAdmin ? 8 : 4;

    const {
        register,
        setValue,
        formState: { errors },
    } = useForm();

    const handleValidar = () => {
        if (!biometriaSrc) {
            setErroBioface("A bioface é obrigatória");
            return false;
        }

        setErroBioface("");

        if (!justBioface) {
            if (!senha || !confirmarSenha) {
                setErroSenha("Por favor, preencha todos os campos");
                return false;
            }

            if (senha.length < minSenha) {
                setErroSenha(`A senha deve ter no mínimo ${minSenha} caracteres`);
                return false;
            }

            if (senha !== confirmarSenha) {
                setErroSenha("As senhas não coincidem");
                return false;
            }
        }

        setErroSenha("");
        return true;
    };

    async function handleSubmit() {
        const ok = handleValidar();
        if (!ok) return;

        try {
            exibirDialog(justBioface ? "Atualizando Foto..." : "Definindo Primeiro Acesso...");

            const payload = {
                bio_face_identifier: biometriaSrc,
            };

            if (!justBioface) {
                payload.set_primeiro_acesso = true;
                payload.primeiro_acesso = 0;
                payload.senha = senha;
            }

            const res = await atualizaUsuario(selectedPessoa.id, payload);

            if (res.data?.message) {
                setOpen(false);

                if (reloadPessoa) {
                    await reloadPessoa();
                }

                exibirAlerta(
                    'Sucesso',
                    justBioface
                        ? 'Bioface atualizada com sucesso'
                        : 'Primeiro acesso definido com sucesso',
                    'success'
                );
            }
        } catch (error) {
            const apiError = error?.response?.data;

            if (
                apiError?.error?.includes("1001") ||
                apiError?.error?.includes("1013")
            ) {
                exibirAlerta(
                    'Foto inválida',
                    'A foto enviada não pôde ser processada. Tente novamente com boa iluminação.',
                    'warning'
                );
                return;
            }

            exibirAlerta(
                'Ops',
                apiError?.message || 'Erro ao processar operação',
                'error'
            );
        } finally {
            handleCloseDialog();
        }
    }

    useEffect(() => {
        if (open) {
            setSenha("");
            setConfirmarSenha("");
            setErroSenha("");
            setBiometriaSrc(null);
        }
    }, [open]);

    return (
        <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="lg">
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" component="span">
                    {justBioface ? 'Definir Bioface' : 'Definir Primeiro Acesso'}
                </Typography>
            </DialogTitle>

            <DialogContent>
                <Grid container spacing={3} sx={{ mt: 1 }}>

                    <Grid item xs={12} md={justBioface ? 12 : 6}>
                        <Paper
                            elevation={3}
                            sx={{
                                p: 3,
                                height: '100%',
                                border: '2px solid #1E5139',
                                borderRadius: 2
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                                <Fingerprint sx={{ color: '#1E5139', fontSize: 28 }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1E5139' }}>
                                    Foto
                                </Typography>
                            </Box>

                            <WebcamCapture
                                biometriaSrc={biometriaSrc}
                                setBiometriaSrc={(img) => {
                                    setBiometriaSrc(img);
                                    setValue("bio_face_identifier", img, {
                                        shouldValidate: true,
                                        shouldDirty: true
                                    });
                                }}
                                isAllowedCapture={isAllowedCapture}
                                setIsAllowedCapture={setIsAllowedCapture}
                                isEditAction={true}
                                disabled={!allowInteraction}
                                multiplier={1.5}
                            />

                            {erroBioface && (
                                <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                                    {erroBioface}
                                </Typography>
                            )}
                        </Paper>
                    </Grid>

                    {!justBioface && (
                        <Grid item xs={12} md={6}>
                            <Paper
                                elevation={3}
                                sx={{
                                    p: 3,
                                    height: '100%',
                                    border: '2px solid #1E5139',
                                    borderRadius: 2
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1 }}>
                                    <Lock sx={{ color: '#1E5139', fontSize: 28 }}/>
                                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#1E5139' }}>
                                        Senha
                                    </Typography>
                                </Box>

                                <TextField
                                    fullWidth
                                    type="password"
                                    label="Senha"
                                    value={senha}
                                    onChange={(e) => {
                                        setSenha(e.target.value);
                                        setErroSenha("");
                                    }}
                                    error={!!erroSenha}
                                    inputProps={{
                                        minLength: minSenha,
                                        autoComplete: "new-password"
                                    }}
                                />

                                <TextField
                                    sx={{ mt: 2 }}
                                    fullWidth
                                    type="password"
                                    label="Confirmar Senha"
                                    value={confirmarSenha}
                                    onChange={(e) => {
                                        setConfirmarSenha(e.target.value);
                                        setErroSenha("");
                                    }}
                                    error={!!erroSenha}
                                    helperText={erroSenha}
                                    inputProps={{
                                        minLength: minSenha,
                                        autoComplete: "new-password"
                                    }}
                                />

                                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                                    • Mínimo de {minSenha} caracteres
                                </Typography>
                            </Paper>
                        </Grid>
                    )}

                </Grid>
            </DialogContent>

            <DialogActions>
                <Button variant="outlined" onClick={() => setOpen(false)}>
                    Cancelar
                </Button>

                <Button variant="contained" onClick={handleSubmit}>
                    Salvar
                </Button>
            </DialogActions>
        </Dialog>
    );
}